import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoAdd, IoChevronDown, IoEllipsisHorizontal, IoRadio } from 'react-icons/io5';
import MakePeriodFromDraftModal from './MakePeriodFromDraftModal';
import PortaledMenu from '../ui/PortaledMenu';
import {
    formatHistoryTimestamp,
    formatVersionLabel,
    generateVersionName,
    getDraftTitle,
    isHiddenFromVersions,
    isHistoryDraft
} from '../../utils/scheduleDraftKind';
import {
    formatPeriodBoundsLabel,
    formatPeriodShortMonthYearRange
} from '../../utils/yearStructurePeriodUtils';

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const getPeriodActiveDraftId = (period) => {
    const raw = period?.active_schedule_draft_id ?? period?.activeScheduleDraftId;
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    return raw;
};

const draftMatchesPeriod = (draft, periodId) => {
    if (periodId === '__unassigned__') {
        return getDraftPeriodId(draft) === null;
    }

    const draftPeriodId = getDraftPeriodId(draft);
    if (!draftPeriodId) return false;
    return String(draftPeriodId) === String(periodId);
};

const getPeriodCalendarYear = (period) => {
    if (!period) return null;
    return period.year ?? period.start_week_year ?? period.startWeekYear ?? null;
};

const DraftPeriodSidebar = ({
    currentDraftName,
    draftManager,
    onModeChange,
    onDraftChange,
    onToggleDraftLive,
    onCurrentDraftDeleted,
    refreshTimePeriods,
    refreshScheduleEntries,
    timePeriods = [],
    selectedPeriodId,
    selectedYearForView,
    setError,
    setSuccess,
    setIsLoading
}) => {
    const [drafts, setDrafts] = useState([]);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [openMenuDraftName, setOpenMenuDraftName] = useState(null);
    const [openMenuAnchorEl, setOpenMenuAnchorEl] = useState(null);
    const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(false);
    const [showDraftCreator, setShowDraftCreator] = useState(false);
    const [newDraftName, setNewDraftName] = useState('');
    const [creatingDraft, setCreatingDraft] = useState(false);
    const [makePeriodDraft, setMakePeriodDraft] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const newDraftNameInputRef = useRef(null);
    const periodMenuRef = useRef(null);
    const [filterPeriodId, setFilterPeriodId] = useState(() => {
        if (selectedPeriodId != null && selectedPeriodId !== '') {
            return String(selectedPeriodId);
        }
        return timePeriods[0]?.id != null ? String(timePeriods[0].id) : '';
    });

    const loadDrafts = useCallback(async () => {
        if (!draftManager) return;
        setLoadingDrafts(true);
        try {
            const draftList = await draftManager.fetchDrafts();
            setDrafts(draftList || []);
        } catch (err) {
            console.error('Error loading drafts:', err);
        } finally {
            setLoadingDrafts(false);
        }
    }, [draftManager]);

    useEffect(() => {
        loadDrafts();
    }, [loadDrafts]);

    useEffect(() => {
        if (!draftManager) return undefined;

        const handleDraftManagerChange = (state) => {
            if (state.drafts) {
                setDrafts(state.drafts || []);
            }
        };

        draftManager.addListener(handleDraftManagerChange);
        return () => draftManager.removeListener(handleDraftManagerChange);
    }, [draftManager]);

    useEffect(() => {
        if (!currentDraftName) {
            if (selectedPeriodId != null && selectedPeriodId !== '') {
                setFilterPeriodId(String(selectedPeriodId));
            }
            return;
        }

        const currentDraft = drafts.find((draft) => draft.draft_name === currentDraftName);
        const draftPeriodId = getDraftPeriodId(currentDraft);
        if (draftPeriodId != null) {
            setFilterPeriodId(String(draftPeriodId));
            return;
        }

        if (selectedPeriodId != null && selectedPeriodId !== '') {
            setFilterPeriodId(String(selectedPeriodId));
        }
    }, [currentDraftName, drafts, selectedPeriodId]);

    const hasUnscopedDrafts = useMemo(
        () => drafts.some((draft) => !isHiddenFromVersions(draft) && getDraftPeriodId(draft) === null),
        [drafts]
    );

    const periodOptions = useMemo(() => {
        const options = (timePeriods || []).map((period) => ({
            id: String(period.id),
            name: period.name || 'Period',
            timeframe: formatPeriodShortMonthYearRange(period),
            bounds: formatPeriodBoundsLabel(period),
            color: period.color || '#3B82F6',
            period
        }));

        if (hasUnscopedDrafts) {
            options.push({
                id: '__unassigned__',
                name: 'Unscoped drafts',
                timeframe: '',
                bounds: '',
                color: '#9CA3AF',
                period: null
            });
        }

        return options;
    }, [timePeriods, hasUnscopedDrafts]);

    const selectedPeriod = useMemo(() => {
        if (filterPeriodId === '__unassigned__') return null;
        return (timePeriods || []).find((period) => String(period.id) === String(filterPeriodId)) || null;
    }, [filterPeriodId, timePeriods]);

    const selectedPeriodTitle = useMemo(() => {
        if (filterPeriodId === '__unassigned__') return 'Unscoped drafts';
        return selectedPeriod?.name || (periodOptions.length === 0 ? 'No periods' : 'Select period');
    }, [filterPeriodId, selectedPeriod, periodOptions.length]);

    const selectedPeriodTimeframe = useMemo(() => {
        if (!selectedPeriod) return '';
        return formatPeriodShortMonthYearRange(selectedPeriod);
    }, [selectedPeriod]);

    const selectedPeriodBounds = useMemo(() => {
        if (!selectedPeriod) return '';
        return formatPeriodBoundsLabel(selectedPeriod);
    }, [selectedPeriod]);

    const selectedPeriodColor = selectedPeriod?.color || '#3B82F6';

    const closeDraftMenu = useCallback(() => {
        setOpenMenuDraftName(null);
        setOpenMenuAnchorEl(null);
    }, []);

    const toggleDraftMenu = (key, event) => {
        event.stopPropagation();
        if (openMenuDraftName === key) {
            closeDraftMenu();
            return;
        }
        setIsPeriodMenuOpen(false);
        setOpenMenuDraftName(key);
        setOpenMenuAnchorEl(event.currentTarget);
    };

    const filteredDrafts = useMemo(() => {
        if (!filterPeriodId) return [];
        return drafts.filter((draft) => draftMatchesPeriod(draft, filterPeriodId));
    }, [drafts, filterPeriodId]);

    const versionDrafts = useMemo(
        () => filteredDrafts.filter((draft) => !isHiddenFromVersions(draft)),
        [filteredDrafts]
    );

    const historyDrafts = useMemo(
        () => filteredDrafts
            .filter((draft) => isHistoryDraft(draft))
            .sort((a, b) => new Date(b.created_at || b.last_modified || 0) - new Date(a.created_at || a.last_modified || 0)),
        [filteredDrafts]
    );

    const activeDraftIdForFilterPeriod = useMemo(
        () => getPeriodActiveDraftId(selectedPeriod),
        [selectedPeriod]
    );

    const liveDraft = useMemo(() => {
        if (!activeDraftIdForFilterPeriod) return null;
        return filteredDrafts.find((draft) => {
            const draftId = getDraftId(draft);
            return draftId && String(draftId) === String(activeDraftIdForFilterPeriod);
        }) || null;
    }, [activeDraftIdForFilterPeriod, filteredDrafts]);

    const displayedVersionDrafts = useMemo(() => {
        if (!liveDraft) return versionDrafts;
        const liveId = getDraftId(liveDraft);
        return versionDrafts.filter((draft) => String(getDraftId(draft)) !== String(liveId));
    }, [liveDraft, versionDrafts]);

    const isDraftActiveForPeriod = useCallback((draft) => {
        const draftId = getDraftId(draft);
        if (!draftId || !activeDraftIdForFilterPeriod) return false;
        return String(draftId) === String(activeDraftIdForFilterPeriod);
    }, [activeDraftIdForFilterPeriod]);

    const handleLoadDraft = async (draftName) => {
        if (!draftName || draftName === currentDraftName) return;

        if (setIsLoading) setIsLoading(true);
        try {
            if (draftManager) {
                draftManager.setDraftMode(draftName);
            }
            onModeChange(false, draftName);

            if (setSuccess) {
                setSuccess(`Loaded draft "${draftName}"`);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err) {
            if (setError) setError('Failed to load draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleToggleLiveDraft = async (draft, isActive) => {
        if (typeof onToggleDraftLive !== 'function') {
            if (setError) setError('Cannot update live draft: live draft handler is unavailable.');
            return;
        }

        try {
            const result = await onToggleDraftLive(draft, isActive, {
                savePendingFirst: isHistoryDraft(draft) ? false : undefined,
                onAfterSuccess: () => closeDraftMenu()
            });
            if (result === true) closeDraftMenu();
        } catch (err) {
            if (setError) setError(`Failed to ${isActive ? 'deactivate' : 'activate'} draft: ${err.message}`);
        }
    };

    const handleDuplicateDraft = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const internalName = generateVersionName(selectedPeriod, drafts);
        if (setIsLoading) setIsLoading(true);
        try {
            const result = await draftManager.duplicateDraft(draft.draft_name, internalName);
            const title = result?.display_name || getDraftTitle(draft);
            const versionLabel = formatVersionLabel(result?.version_number);
            if (setSuccess) {
                setSuccess(`Duplicated "${title}"${versionLabel ? ` (${versionLabel})` : ''}.`);
                setTimeout(() => setSuccess(null), 3000);
            }
            closeDraftMenu();
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to duplicate draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleOpenAsVersion = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const newName = generateVersionName(selectedPeriod, drafts);
        if (setIsLoading) setIsLoading(true);
        try {
            const result = await draftManager.duplicateDraft(draft.draft_name, newName);
            onModeChange(false, newName);
            const title = result?.display_name || getDraftTitle(draft);
            const versionLabel = formatVersionLabel(result?.version_number);
            if (setSuccess) {
                setSuccess(`Opened as "${title}"${versionLabel ? ` (${versionLabel})` : ''}. Live is unchanged until you make this version live.`);
                setTimeout(() => setSuccess(null), 4000);
            }
            closeDraftMenu();
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to open as a new version: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleRenameDraft = async (draft) => {
        if (!draftManager || !draft?.draft_name) return;

        const currentTitle = getDraftTitle(draft);
        const newName = window.prompt('Rename draft:', currentTitle);
        if (!newName || !newName.trim() || newName.trim() === currentTitle) return;

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.renameDraft(draft.draft_name, newName.trim());
            if (setSuccess) {
                setSuccess(`Draft renamed to "${newName.trim()}".`);
                setTimeout(() => setSuccess(null), 3000);
            }
            closeDraftMenu();
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to rename draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const handleDeleteDraft = async (draft, isActive) => {
        if (!draftManager || !draft?.draft_name) return;

        const label = isHistoryDraft(draft)
            ? formatHistoryTimestamp(draft.created_at || draft.last_modified)
            : getDraftTitle(draft);

        if (isActive) {
            if (setError) setError(`Remove from Live before deleting "${label}".`);
            return;
        }

        const confirmed = window.confirm(`Delete "${label}"? This cannot be undone.`);
        if (!confirmed) return;

        if (setIsLoading) setIsLoading(true);
        try {
            await draftManager.deleteDraft(draft.draft_name);
            if (currentDraftName === draft.draft_name) {
                if (typeof onCurrentDraftDeleted === 'function') {
                    await onCurrentDraftDeleted();
                }
            }
            if (setSuccess) {
                setSuccess(`"${label}" deleted.`);
                setTimeout(() => setSuccess(null), 3000);
            }
            closeDraftMenu();
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to delete draft: ' + err.message);
        } finally {
            if (setIsLoading) setIsLoading(false);
        }
    };

    const currentDraftInFilter = displayedVersionDrafts.some((draft) => draft.draft_name === currentDraftName)
        || liveDraft?.draft_name === currentDraftName
        || historyDrafts.some((draft) => draft.draft_name === currentDraftName);

    const handlePeriodCreatedFromDraft = async ({ period }) => {
        const attachedDraftName = makePeriodDraft?.draft_name;
        if (refreshTimePeriods) {
            await refreshTimePeriods();
        }
        if (period?.id != null) {
            setFilterPeriodId(String(period.id));
        }
        setIsPeriodMenuOpen(false);
        setMakePeriodDraft(null);
        await loadDrafts();
        if (typeof refreshScheduleEntries === 'function') {
            try {
                await refreshScheduleEntries();
            } catch (_) {
                // Period and draft association already succeeded.
            }
        }
        if (setSuccess) {
            const periodLabel = period?.name ? `"${period.name}"` : 'Period';
            setSuccess(attachedDraftName
                ? `${periodLabel} created and "${attachedDraftName}" attached. It is not live yet.`
                : `${periodLabel} added successfully.`);
            setTimeout(() => setSuccess(null), 4000);
        }
    };

    const makePeriodLivePeriodName = useMemo(() => {
        if (!makePeriodDraft) return '';
        const draftId = getDraftId(makePeriodDraft);
        if (!draftId) return '';
        const livePeriod = (timePeriods || []).find((period) => {
            const activeDraftId = getPeriodActiveDraftId(period);
            return activeDraftId && String(activeDraftId) === String(draftId);
        });
        return livePeriod?.name || '';
    }, [makePeriodDraft, timePeriods]);

    const makePeriodSourcePeriod = useMemo(() => {
        const periodId = getDraftPeriodId(makePeriodDraft);
        if (!periodId) return null;
        return (timePeriods || []).find((period) => String(period.id) === String(periodId))
            || makePeriodDraft?.period
            || null;
    }, [makePeriodDraft, timePeriods]);

    const openMakePeriodDialog = (draft) => {
        closeDraftMenu();
        setMakePeriodDraft(draft);
    };

    const openDraftCreator = () => {
        if (showDraftCreator) {
            setShowDraftCreator(false);
            setNewDraftName('');
            return;
        }

        setNewDraftName(selectedPeriod?.name || 'Schedule');
        setShowDraftCreator(true);
    };

    useEffect(() => {
        if (showDraftCreator) {
            newDraftNameInputRef.current?.focus();
            newDraftNameInputRef.current?.select();
        }
    }, [showDraftCreator]);

    const handleCreateDraft = async () => {
        if (!newDraftName.trim()) {
            if (setError) setError('Please enter a version name');
            return;
        }

        if (!draftManager) {
            if (setError) setError('Draft manager not available');
            return;
        }

        if (!filterPeriodId) {
            if (setError) setError('Select a period before creating a version');
            return;
        }

        const periodId = filterPeriodId === '__unassigned__' ? null : filterPeriodId;
        const draftYear = getPeriodCalendarYear(selectedPeriod) ?? selectedYearForView ?? new Date().getFullYear();

        setCreatingDraft(true);
        if (setIsLoading) setIsLoading(true);
        try {
            const displayName = newDraftName.trim();
            const internalName = generateVersionName(selectedPeriod, drafts);
            await draftManager.createEmptyDraft(internalName, draftYear, periodId, { display_name: displayName });
            onModeChange(false, internalName);
            setShowDraftCreator(false);
            setNewDraftName('');
            if (setSuccess) {
                setSuccess(`Version "${displayName}" created.`);
                setTimeout(() => setSuccess(null), 3000);
            }
            await loadDrafts();
        } catch (err) {
            if (setError) setError('Failed to create version: ' + err.message);
        } finally {
            setCreatingDraft(false);
            if (setIsLoading) setIsLoading(false);
        }
    };

    const isLiveSelected = Boolean(liveDraft && liveDraft.draft_name === currentDraftName);
    const isLiveHistoryItem = isHistoryDraft(liveDraft);
    const liveLabel = getDraftTitle(liveDraft);
    const liveVersionLabel = formatVersionLabel(liveDraft?.version_number);
    const liveMenuKey = liveDraft ? `live:${liveDraft.draft_name}` : null;

    return (
        <aside className="flex w-80 flex-none flex-col py-4 px-2 min-h-0">
            <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
                <div className="flex min-h-0 flex-1 flex-col overflow-visible rounded-2xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white">
                    <div
                        ref={periodMenuRef}
                        className="relative z-20 flex-none border-b border-gray-200/80"
                    >
                        <button
                            type="button"
                            onClick={() => {
                                if (periodOptions.length === 0) return;
                                closeDraftMenu();
                                setIsPeriodMenuOpen((open) => !open);
                            }}
                            disabled={periodOptions.length === 0}
                            className="group flex w-full items-start gap-3 rounded-t-2xl px-3.5 py-3.5 text-left transition-colors hover:bg-white/70 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-haspopup="listbox"
                            aria-expanded={isPeriodMenuOpen}
                        >
                            <span
                                className="mt-1.5 h-3 w-3 flex-none rounded-full ring-2 ring-white shadow-sm"
                                style={{ backgroundColor: selectedPeriodColor }}
                                aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-400">
                                    Schedule period
                                </p>
                                <div className="mt-0.5 flex items-center gap-1.5">
                                    <h2 className="truncate text-lg font-semibold leading-tight text-gray-900">
                                        {selectedPeriodTitle}
                                    </h2>
                                    <IoChevronDown
                                        className={`h-4 w-4 flex-none text-gray-400 transition-transform group-hover:text-gray-600 ${isPeriodMenuOpen ? 'rotate-180' : ''}`}
                                        aria-hidden="true"
                                    />
                                </div>
                                {selectedPeriodTimeframe ? (
                                    <p className="mt-1 text-sm text-gray-500">
                                        {selectedPeriodTimeframe}
                                        {selectedPeriodBounds ? (
                                            <span className="text-gray-400"> · {selectedPeriodBounds}</span>
                                        ) : null}
                                    </p>
                                ) : filterPeriodId === '__unassigned__' ? (
                                    <p className="mt-1 text-sm text-gray-500">Not attached to a period</p>
                                ) : null}
                            </div>
                        </button>

                        <PortaledMenu
                            isOpen={isPeriodMenuOpen}
                            onClose={() => setIsPeriodMenuOpen(false)}
                            anchorRef={periodMenuRef}
                            matchAnchorWidth
                            offsetY={4}
                            role="listbox"
                            className="max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                        >
                            {periodOptions.map((option) => {
                                const isSelectedOption = String(option.id) === String(filterPeriodId);
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        role="option"
                                        aria-selected={isSelectedOption}
                                        onClick={() => {
                                            setFilterPeriodId(option.id);
                                            setIsPeriodMenuOpen(false);
                                        }}
                                        className={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors ${isSelectedOption ? 'bg-blue-50' : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        <span
                                            className="mt-1 h-2.5 w-2.5 flex-none rounded-full"
                                            style={{ backgroundColor: option.color }}
                                            aria-hidden="true"
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className={`block truncate text-sm ${isSelectedOption ? 'font-semibold text-blue-900' : 'font-medium text-gray-900'}`}>
                                                {option.name}
                                            </span>
                                            {option.timeframe ? (
                                                <span className="mt-0.5 block text-xs text-gray-500">
                                                    {option.timeframe}
                                                </span>
                                            ) : null}
                                        </span>
                                    </button>
                                );
                            })}
                        </PortaledMenu>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-2.5">
                        {selectedPeriod ? (
                            <div className="mb-3">
                                <div className="mb-2 flex items-center gap-2 pl-1">
                                    <span className="h-4 w-px bg-green-400" aria-hidden="true" />
                                    <IoRadio className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
                                    <h3 className="text-xs font-semibold uppercase tracking-[0.08em] text-green-700">
                                        Live
                                    </h3>
                                </div>

                                {liveDraft ? (
                                    <div
                                        className={`relative ml-1 overflow-hidden rounded-xl border-2 shadow-sm transition-all ${isLiveSelected
                                            ? 'border-green-500 bg-gradient-to-br from-green-50 via-white to-green-100/70'
                                            : 'border-green-300 bg-gradient-to-br from-green-50 via-white to-green-50/80 hover:border-green-400'
                                            }`}
                                        title={`${liveLabel} is live for this period`}
                                    >
                                        <span className="absolute inset-y-0 left-0 w-1.5 bg-green-500" aria-hidden="true" />
                                        <button
                                            type="button"
                                            onClick={() => handleLoadDraft(liveDraft.draft_name)}
                                            className="w-full py-3 pl-4 pr-10 text-left"
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="relative flex h-2.5 w-2.5 flex-none">
                                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-70" />
                                                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
                                                </span>
                                                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
                                                    {liveLabel}
                                                </span>
                                                {liveVersionLabel ? (
                                                    <span className="flex-none text-[11px] font-medium text-gray-500">
                                                        {liveVersionLabel}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <p className="mt-1 pl-[1.125rem] text-[11px] font-medium text-green-800">
                                                Published — what people see
                                            </p>
                                            {isLiveHistoryItem ? (
                                                <p className="mt-0.5 pl-[1.125rem] text-[11px] text-gray-500">
                                                    restored from {formatHistoryTimestamp(liveDraft.created_at || liveDraft.last_modified)}
                                                </p>
                                            ) : null}
                                            {isLiveSelected && (
                                                <p className="mt-0.5 pl-[1.125rem] text-xs font-medium text-blue-700">
                                                    Currently open
                                                </p>
                                            )}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={(event) => toggleDraftMenu(liveMenuKey, event)}
                                            className="absolute right-1.5 top-1.5 rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-gray-700 hover:shadow-sm"
                                            aria-label={`Open actions for ${liveLabel}`}
                                            title="Draft actions"
                                        >
                                            <IoEllipsisHorizontal className="h-4 w-4" />
                                        </button>

                                        <PortaledMenu
                                            isOpen={openMenuDraftName === liveMenuKey}
                                            onClose={closeDraftMenu}
                                            anchorEl={openMenuAnchorEl}
                                            align="right"
                                            className="w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleToggleLiveDraft(liveDraft, true)}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                            >
                                                Remove from Live
                                            </button>
                                            {isLiveHistoryItem ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenAsVersion(liveDraft)}
                                                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                >
                                                    Open as new version
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => openMakePeriodDialog(liveDraft)}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                    >
                                                        Make it a Period...
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDuplicateDraft(liveDraft)}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                    >
                                                        Duplicate
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRenameDraft(liveDraft)}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                    >
                                                        Rename
                                                    </button>
                                                </>
                                            )}
                                            <div className="my-1 border-t border-gray-100" />
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteDraft(liveDraft, true)}
                                                disabled
                                                className="w-full cursor-not-allowed px-3 py-2 text-left text-sm text-gray-400"
                                            >
                                                Remove from Live to delete
                                            </button>
                                        </PortaledMenu>
                                    </div>
                                ) : (
                                    <div className="ml-1 rounded-xl border border-dashed border-green-200 bg-green-50/40 px-3 py-3">
                                        <p className="text-sm font-medium text-green-900">Nothing live yet</p>
                                        <p className="mt-0.5 text-xs text-green-800/80">
                                            Create a version and Set Live.
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : null}

                        <div className="mb-2 flex items-center justify-between pl-1">
                            <div className="flex items-center gap-2">
                                <span className="h-4 w-px bg-gray-300" aria-hidden="true" />
                                <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500">
                                    Versions
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={openDraftCreator}
                                disabled={!filterPeriodId || creatingDraft}
                                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                                aria-label={showDraftCreator ? 'Close version creator' : 'Add version'}
                                title={showDraftCreator ? 'Close' : 'Add version'}
                            >
                                <IoAdd className={`h-4 w-4 transition-transform ${showDraftCreator ? 'rotate-45' : ''}`} />
                            </button>
                        </div>

                        {showDraftCreator && (
                            <div className="mb-2 ml-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Name
                                </label>
                                <input
                                    ref={newDraftNameInputRef}
                                    type="text"
                                    value={newDraftName}
                                    onChange={(event) => setNewDraftName(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleCreateDraft();
                                        }
                                        if (event.key === 'Escape') {
                                            event.preventDefault();
                                            setShowDraftCreator(false);
                                            setNewDraftName('');
                                        }
                                    }}
                                    placeholder="Enter version name..."
                                    disabled={creatingDraft}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                />
                                <div className="mt-2.5 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCreateDraft}
                                        disabled={creatingDraft || !newDraftName.trim()}
                                        className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {creatingDraft ? 'Creating...' : 'Create'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowDraftCreator(false);
                                            setNewDraftName('');
                                        }}
                                        disabled={creatingDraft}
                                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {!currentDraftInFilter && currentDraftName ? (
                            <div className="mb-2 ml-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                Current draft is scoped to another period.
                            </div>
                        ) : null}

                        <div className="relative ml-1 space-y-1.5 border-l border-gray-200 pl-3">
                            {loadingDrafts && displayedVersionDrafts.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 px-3 py-5 text-center text-sm text-gray-400">
                                    Loading versions…
                                </div>
                            ) : displayedVersionDrafts.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 px-3 py-5 text-center text-sm text-gray-400">
                                    No versions yet
                                </div>
                            ) : (
                                displayedVersionDrafts.map((draft) => {
                                    const isActive = isDraftActiveForPeriod(draft);
                                    const isSelected = draft.draft_name === currentDraftName;
                                    const isHistoryItem = isHistoryDraft(draft);

                                    return (
                                        <div
                                            key={draft.draft_name}
                                            className={`relative rounded-xl border transition-all ${isSelected
                                                ? 'border-blue-300 bg-blue-50 shadow-sm'
                                                : 'border-gray-200/80 bg-white hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                                title={isHistoryItem ? formatHistoryTimestamp(draft.created_at || draft.last_modified) : getDraftTitle(draft)}
                                        >
                                            <span
                                                className="absolute -left-[0.8125rem] top-1/2 h-px w-3 -translate-y-1/2 bg-gray-200"
                                                aria-hidden="true"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleLoadDraft(draft.draft_name)}
                                                className="w-full px-3 py-2.5 pr-10 text-left"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className={`h-2 w-2 flex-none rounded-full ${isSelected ? 'bg-blue-500' : 'bg-gray-300'}`} />
                                                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                                                        {isHistoryItem
                                                            ? formatHistoryTimestamp(draft.created_at || draft.last_modified)
                                                            : (formatVersionLabel(draft.version_number)
                                                                ? `${getDraftTitle(draft)} · ${formatVersionLabel(draft.version_number)}`
                                                                : getDraftTitle(draft))}
                                                    </span>
                                                </div>
                                                {isSelected && (
                                                    <p className="mt-1 pl-4 text-xs font-medium text-blue-700">
                                                        Currently open
                                                    </p>
                                                )}
                                            </button>

                                            <button
                                                type="button"
                                                onClick={(event) => toggleDraftMenu(draft.draft_name, event)}
                                                className="absolute right-1.5 top-1.5 rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-gray-700 hover:shadow-sm"
                                                aria-label={`Open actions for ${getDraftTitle(draft)}`}
                                                title="Draft actions"
                                            >
                                                <IoEllipsisHorizontal className="h-4 w-4" />
                                            </button>

                                            <PortaledMenu
                                                isOpen={openMenuDraftName === draft.draft_name}
                                                onClose={closeDraftMenu}
                                                anchorEl={openMenuAnchorEl}
                                                align="right"
                                                className="w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggleLiveDraft(draft, isActive)}
                                                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                >
                                                    {isActive ? 'Remove from Live' : 'Set Live'}
                                                </button>
                                                {isHistoryItem ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleOpenAsVersion(draft)}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                    >
                                                        Open as new version
                                                    </button>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => openMakePeriodDialog(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Make it a New Period...
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDuplicateDraft(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Duplicate
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRenameDraft(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Rename
                                                        </button>
                                                    </>
                                                )}
                                                <div className="my-1 border-t border-gray-100" />
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteDraft(draft, isActive)}
                                                    disabled={isActive}
                                                    className={`w-full px-3 py-2 text-left text-sm ${isActive ? 'cursor-not-allowed text-gray-400' : 'text-red-600 hover:bg-red-50'}`}
                                                >
                                                    {isActive ? 'Remove from Live to delete' : 'Delete'}
                                                </button>
                                            </PortaledMenu>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="mt-3 ml-1">
                            <button
                                type="button"
                                onClick={() => setIsHistoryOpen((open) => !open)}
                                className="flex w-full items-center justify-between rounded-lg px-1 py-1.5 text-left text-xs font-semibold uppercase tracking-[0.06em] text-gray-500 hover:text-gray-700"
                            >
                                <span>History{historyDrafts.length ? ` (${historyDrafts.length})` : ''}</span>
                                <IoChevronDown className={`h-3.5 w-3.5 transition-transform ${isHistoryOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isHistoryOpen && (
                                <div className="mt-1 space-y-1.5">
                                    {historyDrafts.length === 0 ? (
                                        <div className="rounded-xl border border-dashed border-gray-200 bg-white/70 px-3 py-4 text-center text-xs text-gray-400">
                                            No restore points yet
                                        </div>
                                    ) : (
                                        historyDrafts.map((draft) => {
                                            const isActive = isDraftActiveForPeriod(draft);
                                            const successor = drafts.find((item) => String(item.id) === String(draft.replaced_by_draft_id));
                                            const replacedByLabel = successor
                                                ? getDraftTitle(successor)
                                                : (draft.replaced_by_display_name
                                                    || (draft.replaced_by_draft_name && !String(draft.replaced_by_draft_name).startsWith('__history__-')
                                                        && !String(draft.replaced_by_draft_name).startsWith('__v__-')
                                                        ? draft.replaced_by_draft_name
                                                        : (draft.replaced_by_draft_id ? 'a restored snapshot' : null)));
                                            const wasLabel = [
                                                getDraftTitle(draft),
                                                formatVersionLabel(draft.version_number)
                                            ].filter(Boolean).join(' · ');
                                            const historyLabel = formatHistoryTimestamp(draft.created_at || draft.last_modified);
                                            return (
                                                <div
                                                    key={draft.draft_name}
                                                    className={`relative rounded-xl border ${isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!isActive) handleToggleLiveDraft(draft, false);
                                                        }}
                                                        className="w-full px-3 py-2 pr-10 text-left"
                                                        title="Preview and restore this snapshot"
                                                    >
                                                        <div className="truncate text-sm font-medium text-gray-900">
                                                            {historyLabel}
                                                        </div>
                                                        <div className="mt-0.5 text-[11px] text-gray-500">
                                                            {isActive
                                                                ? 'Currently live'
                                                                : replacedByLabel
                                                                    ? `was ${wasLabel} → replaced by ${replacedByLabel}`
                                                                    : `was ${wasLabel}`}
                                                        </div>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(event) => toggleDraftMenu(`history:${draft.draft_name}`, event)}
                                                        className="absolute right-1.5 top-1.5 rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700"
                                                        aria-label={`Open actions for history ${historyLabel}`}
                                                    >
                                                        <IoEllipsisHorizontal className="h-4 w-4" />
                                                    </button>
                                                    <PortaledMenu
                                                        isOpen={openMenuDraftName === `history:${draft.draft_name}`}
                                                        onClose={closeDraftMenu}
                                                        anchorEl={openMenuAnchorEl}
                                                        align="right"
                                                        className="w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (!isActive) handleToggleLiveDraft(draft, false);
                                                            }}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            {isActive ? 'Already live' : 'Restore'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleOpenAsVersion(draft)}
                                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                        >
                                                            Open as new version
                                                        </button>
                                                        {!isActive && (
                                                            <>
                                                                <div className="my-1 border-t border-gray-100" />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteDraft(draft, false)}
                                                                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </>
                                                        )}
                                                    </PortaledMenu>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <MakePeriodFromDraftModal
                isOpen={makePeriodDraft != null}
                onClose={() => setMakePeriodDraft(null)}
                draft={makePeriodDraft}
                sourcePeriod={makePeriodSourcePeriod}
                livePeriodName={makePeriodLivePeriodName}
                onSuccess={handlePeriodCreatedFromDraft}
                setError={setError}
            />
        </aside>
    );
};

export default DraftPeriodSidebar;
